import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Brain, RefreshCw, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";

function InsightsCategories({ categories }) {
  const max = Math.max(1, ...categories.map((c) => Number(c.total) || 0));
  return (
    <div className="md:col-span-5 space-y-2" data-testid="insights-categories">
      {categories.map((c) => (
        <div key={c.name}>
          <div className="flex justify-between text-xs">
            <span className="text-[#A0A7B0]">{c.name}</span>
            <span className="font-mono tabular-nums text-[#F4F1EA]">${Number(c.total || 0).toFixed(0)}/mo</span>
          </div>
          <div className="h-1.5 mt-1 rounded-full bg-[#0C0D0E] overflow-hidden">
            <div className="h-full bg-[#00E676]/80 rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, (Number(c.total || 0) / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TopAction({ action, leak, onCancel, onRefund }) {
  if (!action) return null;
  const canAct = leak && leak.status === "active";
  return (
    <div className="mt-3 p-3 rounded-md border border-[#00E676]/30 bg-[#00E676]/5 flex items-center justify-between gap-3" data-testid="insights-top-action">
      <div className="min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">TONIGHT'S MOVE</div>
        <div className="text-sm text-[#F4F1EA] mt-0.5"><span className="font-semibold">{action.action} {action.merchant}</span> — {action.why}</div>
      </div>
      {canAct && (
        <Button size="sm" className="btn-emerald h-8 shrink-0" onClick={() => (action.action === "Refund" ? onRefund(leak) : onCancel(leak))} data-testid="insights-top-action-button">
          {action.action} <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      )}
    </div>
  );
}

function InsightsObservations({ observations, children }) {
  return (
    <div className="md:col-span-7 space-y-2" data-testid="insights-observations">
      {observations.map((o, i) => (
        <div key={o} className="flex gap-2 text-sm text-[#A0A7B0]">
          <span className="font-mono text-[10px] mt-1 text-[#606872]">{String(i + 1).padStart(2, "0")}</span>
          <span>{o}</span>
        </div>
      ))}
      {children}
    </div>
  );
}

function InsightsSkeleton() {
  return (
    <div className="mt-4 space-y-3 animate-pulse" data-testid="insights-loading">
      <div className="h-7 w-3/4 rounded bg-[#1E2226]" />
      <div className="h-4 w-1/2 rounded bg-[#1E2226]" />
      <div className="h-4 w-2/3 rounded bg-[#1E2226]" />
    </div>
  );
}

export default function InsightsCard({ leaks, onCancel, onRefund }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data: d } = refresh ? await api.post("/insights/refresh") : await api.get("/insights");
      setData(d);
      if (refresh) toast.success("Brief regenerated");
    } catch {
      toast.error("Couldn't load insights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const actionLeak = data?.top_action && leaks.find((l) => l.id === data.top_action.leak_id);

  return (
    <Card className="rounded-md border-[#282C30] bg-[#141618] p-5 sm:p-6 relative overflow-hidden" data-testid="insights-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-[#00E676]" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">NIGHTLY BRIEF · CLAUDE</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => fetchInsights(true)} disabled={loading} className="h-7 text-[#A0A7B0] hover:text-[#F4F1EA]" data-testid="insights-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {!data && loading && <InsightsSkeleton />}
      {data && (
        <>
          <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-3 leading-snug" data-testid="insights-headline">{data.headline}</h3>
          {data.locked && (
            <button onClick={() => onCancel({ locked: true })} className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#00E676] hover:underline" data-testid="insights-locked-cta">
              <Lock className="h-3.5 w-3.5" /> Unlock the full brief
            </button>
          )}
          <div className="grid md:grid-cols-12 gap-5 mt-5">
            <InsightsCategories categories={data.categories || []} />
            <InsightsObservations observations={data.observations || []}>
              <TopAction action={data.top_action} leak={actionLeak} onCancel={onCancel} onRefund={onRefund} />
            </InsightsObservations>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-4" data-testid="insights-generated-at">
            Generated {new Date(data.generated_at).toLocaleString()} · refreshes nightly at 2:00 UTC
          </div>
        </>
      )}
    </Card>
  );
}
